/**
 * Enterprise Mobility Reliability Health Check
 * Strike — Shopify-embeddable assessment
 *
 * Modules (within this IIFE):
 *   Questions   → RATING_OPTIONS, SECTIONS, QUESTIONS, RECOMMENDATIONS
 *   Scoring     → ScoringEngine (pure, UI-independent)
 *   State       → answers object, metrics, step, lead
 *   UI          → sidebar, progress, questions, metrics rendering
 *   Navigation  → back / next / screen transitions
 *   Results     → preview + locked full report
 *   LeadCapture → form gate + Klaviyo-ready hooks
 */

(function () {
  "use strict";

  /* ═══════════════════════════════════════════════════════════
     MODULE: Questions & Content Data
     ═══════════════════════════════════════════════════════════ */

  var RATING_OPTIONS = [
    { label: "Never", value: 1 },
    { label: "Rarely", value: 2 },
    { label: "Sometimes", value: 3 },
    { label: "Often", value: 4 },
    { label: "Very Often", value: 5 }
  ];

  var SECTIONS = [
    { id: "device-reliability", label: "Device Reliability", type: "questions", number: 1 },
    { id: "productivity", label: "Productivity", type: "questions", number: 2 },
    { id: "fleet-standardisation", label: "Fleet Standardisation", type: "questions", number: 3 },
    { id: "safety-compliance", label: "Safety & Compliance", type: "questions", number: 4 },
    { id: "financial-impact", label: "Financial Impact", type: "questions", number: 5 },
    { id: "operational-metrics", label: "Operational Metrics", type: "metrics", number: null },
    { id: "results", label: "Results", type: "results", number: null }
  ];

  var QUESTIONS = [
    {
      id: "q1",
      sectionId: "device-reliability",
      text: "Devices are often unavailable because batteries are flat.",
      finding: "Frequent charging-related downtime",
      findingThreshold: 3,
      tags: ["charging", "reliability"]
    },
    {
      id: "q2",
      sectionId: "device-reliability",
      text: "Devices are damaged due to drops or harsh operating environments.",
      finding: "High exposure to physical device damage",
      findingThreshold: 3,
      tags: ["protection", "reliability"]
    },
    {
      id: "q3",
      sectionId: "device-reliability",
      text: "Accessories frequently fail before the device reaches end of life.",
      finding: "Accessory failure reducing effective device lifespan",
      findingThreshold: 3,
      tags: ["accessories", "lifecycle"]
    },
    {
      id: "q4",
      sectionId: "device-reliability",
      text: "Employees regularly experience interruptions caused by unreliable mobile equipment.",
      finding: "Unreliable equipment interrupting daily operations",
      findingThreshold: 3,
      tags: ["reliability", "productivity"]
    },
    {
      id: "q5",
      sectionId: "productivity",
      text: "Staff lose time waiting for devices to charge.",
      finding: "Productivity loss from charging wait times",
      findingThreshold: 3,
      tags: ["charging", "productivity"]
    },
    {
      id: "q6",
      sectionId: "productivity",
      text: "Staff lose time locating working devices or chargers.",
      finding: "Inconsistent charging infrastructure",
      findingThreshold: 3,
      tags: ["charging", "productivity"]
    },
    {
      id: "q7",
      sectionId: "productivity",
      text: "Workers cannot complete tasks because devices are unavailable.",
      finding: "Task completion blocked by device unavailability",
      findingThreshold: 3,
      tags: ["availability", "productivity"]
    },
    {
      id: "q8",
      sectionId: "productivity",
      text: "Device issues delay customer service or field operations.",
      finding: "Customer and field operations delayed by device issues",
      findingThreshold: 3,
      tags: ["operations", "productivity"]
    },
    {
      id: "q9",
      sectionId: "fleet-standardisation",
      text: "Multiple device models require different accessories and charging solutions.",
      finding: "Poor fleet standardisation",
      findingThreshold: 3,
      tags: ["standardisation", "fleet"]
    },
    {
      id: "q10",
      sectionId: "fleet-standardisation",
      text: "Different business units use different mobility setups.",
      finding: "Fragmented mobility setups across business units",
      findingThreshold: 3,
      tags: ["standardisation", "governance"]
    },
    {
      id: "q11",
      sectionId: "fleet-standardisation",
      text: "IT spends excessive time supporting different configurations.",
      finding: "High IT support overhead",
      findingThreshold: 3,
      tags: ["support", "standardisation"]
    },
    {
      id: "q12",
      sectionId: "safety-compliance",
      text: "Devices are used in vehicles without dedicated mounting solutions.",
      finding: "Vehicles lacking dedicated device mounting",
      findingThreshold: 3,
      tags: ["mounting", "safety"]
    },
    {
      id: "q13",
      sectionId: "safety-compliance",
      text: "Loose cables or charging equipment create workplace hazards.",
      finding: "Workplace hazards from unmanaged charging equipment",
      findingThreshold: 3,
      tags: ["safety", "charging"]
    },
    {
      id: "q14",
      sectionId: "safety-compliance",
      text: "Mobile equipment is not consistently secured during transport.",
      finding: "Inconsistent equipment securing during transport",
      findingThreshold: 3,
      tags: ["safety", "transport"]
    },
    {
      id: "q15",
      sectionId: "financial-impact",
      text: "Devices are replaced before the planned lifecycle.",
      finding: "High device replacement frequency",
      findingThreshold: 3,
      tags: ["lifecycle", "cost"]
    },
    {
      id: "q16",
      sectionId: "financial-impact",
      text: "Mobile technology generates more IT support than expected.",
      finding: "IT support demand exceeding expectations",
      findingThreshold: 3,
      tags: ["support", "cost"]
    },
    {
      id: "q17",
      sectionId: "financial-impact",
      text: "Downtime caused by mobility issues affects operational KPIs.",
      finding: "Mobility downtime impacting operational KPIs",
      findingThreshold: 3,
      tags: ["kpi", "cost"]
    },
    {
      id: "q18",
      sectionId: "financial-impact",
      text: "We believe our mobility environment costs more to operate than it should.",
      finding: "Perceived excess cost in mobility operations",
      findingThreshold: 3,
      tags: ["cost", "governance"]
    }
  ];

  var RECOMMENDATIONS = [
    {
      id: "standardisation",
      title: "Device Standardisation",
      text: "Consolidate device models and accessories to reduce support complexity and improve interchangeability.",
      cta: "Explore standardisation",
      href: "https://www.strike.com.au/contact",
      icon: '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.75"/><rect x="12" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.75"/><rect x="3" y="12" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.75"/><rect x="12" y="12" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.75"/></svg>'
    },
    {
      id: "protection",
      title: "Protection & Ruggedisation",
      text: "Deploy protective cases and rugged accessories matched to your operating environments.",
      cta: "Review protection options",
      href: "https://www.strike.com.au/contact",
      icon: '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 3l7 3v5.5c0 4.2-2.9 7.1-7 8.5-4.1-1.4-7-4.3-7-8.5V6l7-3z" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/></svg>'
    },
    {
      id: "mounting",
      title: "Vehicle Mounting",
      text: "Introduce dedicated in-vehicle mounts to improve safety, ergonomics, and device longevity.",
      cta: "Discuss mounting solutions",
      href: "https://www.strike.com.au/contact",
      icon: '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="4" y="7" width="14" height="9" rx="2" stroke="currentColor" stroke-width="1.75"/><path d="M8 7V5.5A1.5 1.5 0 019.5 4h3A1.5 1.5 0 0114 5.5V7" stroke="currentColor" stroke-width="1.75"/><circle cx="11" cy="11.5" r="1.5" fill="currentColor"/></svg>'
    },
    {
      id: "charging",
      title: "Charging Infrastructure",
      text: "Design multi-bay and shared charging stations so devices stay powered and easy to locate.",
      cta: "Plan charging rollout",
      href: "https://www.strike.com.au/contact",
      icon: '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M12 2L5 12h6l-1 8 7-10h-6l1-8z" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/></svg>'
    },
    {
      id: "lifecycle",
      title: "Lifecycle Management",
      text: "Align refresh cycles, repair policies, and spare pools to extend asset life and control spend.",
      cta: "Optimise lifecycle",
      href: "https://www.strike.com.au/contact",
      icon: '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M17.5 11A6.5 6.5 0 114.8 8.2" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/><path d="M17.5 4.5v4h-4" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    },
    {
      id: "governance",
      title: "Fleet Governance",
      text: "Establish clear standards, ownership, and reporting so mobility spend and risk stay visible.",
      cta: "Strengthen governance",
      href: "https://www.strike.com.au/contact",
      icon: '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M4 18V8l7-4 7 4v10" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/><path d="M9 18v-5h4v5" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/></svg>'
    }
  ];

  var STORAGE_KEY = "strike_mobility_health_check_v2";
  var LEAD_EMAIL_KEY = "strike_mobility_lead_email";
  var ASSESSMENT_PAYLOAD_KEY = "strike_mobility_last_payload";
  var SYNCED_ASSESSMENT_IDS_KEY = "strike_mobility_synced_assessment_ids";
  var ASSESSMENT_WEBHOOK_URL =
    "https://script.google.com/macros/s/AKfycbzGvEbcr_SslJ-14rO6FUJxYqVgX5Dulpi-r2kEgkQR62iQ-TEG_OQ-FE34E69YUjjh-A/exec";
  var KLAVIYO_FORM_ID = "YzpjTn";
  var syncInFlightById = {};

  /* ═══════════════════════════════════════════════════════════
     MODULE: Scoring
     Modify thresholds / weights here without touching UI.
     ═══════════════════════════════════════════════════════════ */

  var ScoringEngine = {
    config: {
      minRating: 1,
      maxRating: 5,
      riskBands: [
        { id: "critical", max: 24, label: "Critical", summary: "Your mobility environment shows critical reliability and cost risks that warrant immediate attention." },
        { id: "high", max: 49, label: "High", summary: "Significant reliability gaps are affecting productivity and likely driving avoidable cost." },
        { id: "moderate", max: 74, label: "Moderate", summary: "There are clear improvement opportunities across reliability, standardisation, and operating cost." },
        { id: "low", max: 100, label: "Low", summary: "Your mobility environment is relatively healthy, with targeted opportunities to further reduce risk and cost." }
      ],
      sectionWeights: {
        "device-reliability": 1,
        "productivity": 1,
        "fleet-standardisation": 1,
        "safety-compliance": 1,
        "financial-impact": 1
      },
      supportTicketCost: 45,
      weeksPerYear: 52
    },

    ratingToHealth: function (rating) {
      var c = this.config;
      var clamped = Math.min(c.maxRating, Math.max(c.minRating, Number(rating) || c.maxRating));
      return ((c.maxRating - clamped) / (c.maxRating - c.minRating)) * 100;
    },

    calculateHealthScore: function (answers) {
      var self = this;
      var totalWeight = 0;
      var weightedSum = 0;

      QUESTIONS.forEach(function (q) {
        var rating = answers[q.id];
        if (rating == null) return;
        var weight = self.config.sectionWeights[q.sectionId] || 1;
        weightedSum += self.ratingToHealth(rating) * weight;
        totalWeight += weight;
      });

      if (totalWeight === 0) return 0;
      return Math.round(weightedSum / totalWeight);
    },

    getRiskLevel: function (score) {
      var bands = this.config.riskBands;
      for (var i = 0; i < bands.length; i++) {
        if (score <= bands[i].max) return bands[i];
      }
      return bands[bands.length - 1];
    },

    getSectionAverages: function (answers) {
      var buckets = {};
      QUESTIONS.forEach(function (q) {
        if (!buckets[q.sectionId]) buckets[q.sectionId] = { sum: 0, count: 0 };
        var rating = answers[q.id];
        if (rating == null) return;
        buckets[q.sectionId].sum += Number(rating);
        buckets[q.sectionId].count += 1;
      });

      var averages = {};
      Object.keys(buckets).forEach(function (id) {
        var b = buckets[id];
        averages[id] = b.count ? b.sum / b.count : 0;
      });
      return averages;
    },

    generateFindings: function (answers) {
      var findings = [];
      var seen = {};

      QUESTIONS.forEach(function (q) {
        var rating = answers[q.id];
        if (rating == null || rating < q.findingThreshold) return;
        if (seen[q.finding]) return;
        seen[q.finding] = true;
        findings.push({
          text: q.finding,
          severity: rating,
          sectionId: q.sectionId,
          tags: q.tags
        });
      });

      findings.sort(function (a, b) {
        return b.severity - a.severity;
      });

      if (findings.length === 0) {
        findings.push({
          text: "Mobility operations appear broadly stable — focus on continuous improvement and governance",
          severity: 1,
          sectionId: null,
          tags: ["governance"]
        });
      }

      return findings.slice(0, 6);
    },

    /** Prioritised recommendation copy derived from top findings / score. */
    generateRecommendationCopy: function (findings, risk) {
      var lines = [];

      if (findings.length) {
        findings.slice(0, 3).forEach(function (f) {
          lines.push("Address “" + f.text.toLowerCase() + "” as a near-term priority in your mobility programme.");
        });
      }

      lines.push("Establish a governed device standard and charging footprint matched to your operating environments.");
      lines.push("Align lifecycle, spares, and support models so refresh spend tracks reliability outcomes.");

      if (risk && (risk.id === "high" || risk.id === "critical")) {
        lines.push("Engage Strike’s Enterprise Mobility specialists to sequence a remediation roadmap against operational KPIs.");
      }

      return lines.slice(0, 5);
    },

    estimateFinancials: function (metrics) {
      var c = this.config;
      var workers = Number(metrics.workers) || 0;
      var hourlyCost = Number(metrics.hourlyCost) || 0;
      var downtimeHours = Number(metrics.downtime) || 0;
      var damaged = Number(metrics.damaged) || 0;
      var replaceCost = Number(metrics.replaceCost) || 0;
      var tickets = Number(metrics.tickets) || 0;

      var annualProductivityLoss = workers * hourlyCost * downtimeHours * c.weeksPerYear;
      var avoidableDeviceCosts = damaged * replaceCost;
      var annualSupportCost = tickets * 12 * c.supportTicketCost;

      return {
        annualProductivityLoss: Math.round(annualProductivityLoss),
        avoidableDeviceCosts: Math.round(avoidableDeviceCosts),
        annualSupportCost: Math.round(annualSupportCost),
        totalExposure: Math.round(annualProductivityLoss + avoidableDeviceCosts + annualSupportCost)
      };
    },

    buildReport: function (answers, metrics) {
      var score = this.calculateHealthScore(answers);
      var risk = this.getRiskLevel(score);
      var findings = this.generateFindings(answers);
      var financials = this.estimateFinancials(metrics);

      return {
        score: score,
        risk: risk,
        findings: findings,
        recommendationCopy: this.generateRecommendationCopy(findings, risk),
        financials: financials,
        sectionAverages: this.getSectionAverages(answers),
        answeredAt: new Date().toISOString()
      };
    }
  };

  /* ═══════════════════════════════════════════════════════════
     MODULE: State & Persistence
     ═══════════════════════════════════════════════════════════ */

  var state = {
    step: "question", /* question | metrics | calculating | results */
    questionIndex: 0,
    answers: {},
    metrics: {
      workers: "",
      hourlyCost: "",
      downtime: "",
      damaged: "",
      replaceCost: "",
      tickets: ""
    },
    direction: "forward",
    report: null,
    lead: null,
    reportUnlocked: false,
    assessmentId: null,
    lastAssessmentPayload: null
  };

  function normalizeEmail(value) {
    if (!value || typeof value !== "string") return "";
    return value.trim().toLowerCase();
  }

  function persistLeadEmail(email) {
    var normalized = normalizeEmail(email);
    if (!normalized) return;
    try {
      sessionStorage.setItem(LEAD_EMAIL_KEY, normalized);
      localStorage.setItem(LEAD_EMAIL_KEY, normalized);
    } catch (e) { /* ignore */ }
  }

  function readPersistedLeadEmail() {
    try {
      return normalizeEmail(
        sessionStorage.getItem(LEAD_EMAIL_KEY) ||
        localStorage.getItem(LEAD_EMAIL_KEY) ||
        ""
      );
    } catch (e) {
      return "";
    }
  }

  function clearPersistedLeadEmail() {
    try {
      sessionStorage.removeItem(LEAD_EMAIL_KEY);
      localStorage.removeItem(LEAD_EMAIL_KEY);
    } catch (e) { /* ignore */ }
  }

  function mergeLead(incoming) {
    var prev = state.lead || {};
    var next = incoming && typeof incoming === "object" ? incoming : {};
    var email = normalizeEmail(next.email || prev.email || readPersistedLeadEmail());
    var merged = {
      source: next.source || prev.source || "klaviyo",
      formId: next.formId || prev.formId || KLAVIYO_FORM_ID,
      email: email,
      firstName: next.firstName || prev.firstName || "",
      lastName: next.lastName || prev.lastName || "",
      company: next.company || prev.company || "",
      phone: next.phone || prev.phone || ""
    };
    if (next.detectedVia && !merged.email) {
      merged.detectedVia = next.detectedVia;
    }
    if (email) persistLeadEmail(email);
    return merged;
  }

  function ensureReportBuilt() {
    var answerCount = state.answers ? Object.keys(state.answers).length : 0;
    if (!answerCount) return null;
    if (!state.report) {
      state.report = ScoringEngine.buildReport(state.answers, state.metrics);
    }
    return state.report;
  }

  function generateAssessmentId() {
    var rand = "";
    try {
      if (window.crypto && window.crypto.getRandomValues) {
        var bytes = new Uint8Array(6);
        window.crypto.getRandomValues(bytes);
        for (var i = 0; i < bytes.length; i++) {
          rand += bytes[i].toString(16).padStart(2, "0");
        }
      }
    } catch (e) { /* ignore */ }
    if (!rand) {
      rand = Math.random().toString(16).slice(2, 14);
    }
    return "mra_" + Date.now().toString(36) + "_" + rand;
  }

  function getSyncedAssessmentIds() {
    try {
      var raw = localStorage.getItem(SYNCED_ASSESSMENT_IDS_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function hasAssessmentBeenSynced(assessmentId) {
    if (!assessmentId) return false;
    if (syncInFlightById[assessmentId]) return true;
    return getSyncedAssessmentIds().indexOf(assessmentId) !== -1;
  }

  function markAssessmentSynced(assessmentId) {
    if (!assessmentId) return;
    var list = getSyncedAssessmentIds();
    if (list.indexOf(assessmentId) === -1) {
      list.push(assessmentId);
      if (list.length > 50) list = list.slice(-50);
      try {
        localStorage.setItem(SYNCED_ASSESSMENT_IDS_KEY, JSON.stringify(list));
      } catch (e) { /* ignore */ }
    }
  }

  /**
   * POST complete assessment payload to Google Apps Script webhook.
   * Failures are logged only — UI unlock must not depend on this.
   * Sends at most once per assessmentId (persisted across refresh).
   */
  function sendAssessmentToWebhook(payload) {
    if (!payload || !payload.assessmentId) return;
    if (hasAssessmentBeenSynced(payload.assessmentId)) return;
    if (typeof fetch !== "function") {
      console.error("[Strike Mobility] Assessment save failed", new Error("fetch is not available"));
      return;
    }

    syncInFlightById[payload.assessmentId] = true;
    console.info("[Strike Mobility] Sending assessment to Google Sheets...");

    /*
     * Omit Content-Type: application/json — that header forces a CORS preflight
     * (OPTIONS), which Google Apps Script web apps do not handle, causing
     * TypeError: Failed to fetch. A string body defaults to text/plain (simple
     * request); Apps Script still receives the JSON in postData.contents.
     */
    fetch(ASSESSMENT_WEBHOOK_URL, {
      method: "POST",
      body: JSON.stringify(payload),
      redirect: "follow"
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error("HTTP " + response.status);
        }
        return response.text().then(function (text) {
          if (text) {
            try {
              var data = JSON.parse(text);
              if (data && data.success === false) {
                throw new Error(data.error || data.message || "Webhook reported failure");
              }
            } catch (parseErr) {
              if (parseErr && parseErr.message && parseErr.message.indexOf("Webhook") === 0) {
                throw parseErr;
              }
              /* Non-JSON 200 responses from Apps Script are treated as success */
            }
          }
          markAssessmentSynced(payload.assessmentId);
          delete syncInFlightById[payload.assessmentId];
          console.info("[Strike Mobility] Assessment saved successfully");
        });
      })
      .catch(function (err) {
        delete syncInFlightById[payload.assessmentId];
        console.error("[Strike Mobility] Assessment save failed", err);
      });
  }

  var consultationInFlight = false;
  var CONSULTATION_SUBMITTED_IDS_KEY = "strike_mobility_consultation_submitted_ids";

  function getConsultationSubmittedIds() {
    try {
      var raw = localStorage.getItem(CONSULTATION_SUBMITTED_IDS_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function hasConsultationBeenSubmitted(assessmentId) {
    if (!assessmentId) return false;
    return getConsultationSubmittedIds().indexOf(assessmentId) !== -1;
  }

  function markConsultationSubmitted(assessmentId) {
    if (!assessmentId) return;
    var list = getConsultationSubmittedIds();
    if (list.indexOf(assessmentId) === -1) {
      list.push(assessmentId);
      if (list.length > 50) list = list.slice(-50);
      try {
        localStorage.setItem(CONSULTATION_SUBMITTED_IDS_KEY, JSON.stringify(list));
      } catch (e) { /* ignore */ }
    }
  }

  function buildConsultationPayload() {
    var lead = mergeLead(state.lead || {});
    var requestedAt = new Date().toISOString();
    return {
      type: "consultation_request",
      assessmentId: state.assessmentId || "",
      requestedAt: requestedAt,
      email: lead.email || "",
      lead: {
        email: lead.email || "",
        firstName: lead.firstName || "",
        lastName: lead.lastName || "",
        company: lead.company || "",
        phone: lead.phone || ""
      },
      klaviyoProperties: {
        mobility_consultation_requested: true,
        mobility_consultation_requested_at: requestedAt
      }
    };
  }

  function postConsultationToWebhook(payload) {
    return fetch(ASSESSMENT_WEBHOOK_URL, {
      method: "POST",
      body: JSON.stringify(payload),
      redirect: "follow"
    }).then(function (response) {
      if (!response.ok) throw new Error("HTTP " + response.status);
      return response.text().then(function (text) {
        if (text) {
          try {
            var data = JSON.parse(text);
            if (data && data.success === false) {
              throw new Error(data.error || data.message || "Webhook reported failure");
            }
          } catch (parseErr) {
            if (parseErr && parseErr.message && (
              parseErr.message.indexOf("Webhook") === 0 ||
              parseErr.message.indexOf("HTTP") === 0
            )) {
              throw parseErr;
            }
            /* Non-JSON SUCCESS text from Apps Script is OK */
          }
        }
      });
    });
  }

  function showConsultationDialog(kind, messageHtml, buttonLabel) {
    var existing = document.getElementById("consultationDialog");
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    var overlay = document.createElement("div");
    overlay.id = "consultationDialog";
    overlay.className =
      "consultation-dialog" +
      (kind === "error" ? " consultation-dialog--error" : "") +
      (kind === "success" ? " consultation-dialog--success" : "");
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "consultationDialogTitle");
    overlay.innerHTML =
      '<div class="consultation-dialog__panel">' +
        '<div class="consultation-dialog__body">' + messageHtml + "</div>" +
        '<div class="consultation-dialog__actions">' +
          '<button type="button" class="btn btn--primary consultation-dialog__btn" id="consultationDialogClose">' +
            escapeHtml(buttonLabel || (kind === "success" ? "Done" : "Close")) +
          "</button>" +
        "</div>" +
      "</div>";

    document.body.appendChild(overlay);

    function close() {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }
    overlay.querySelector("#consultationDialogClose").addEventListener("click", close);
    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) close();
    });
  }

  function showConsultationSuccessDialog() {
    showConsultationDialog(
      "success",
      '<div class="consultation-dialog__icon" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" focusable="false">' +
          '<polyline points="20 6 9 17 4 12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></polyline>' +
        "</svg>" +
      "</div>" +
      '<h2 id="consultationDialogTitle">Thank You for Completing Your Assessment</h2>' +
      '<div class="consultation-dialog__rule" aria-hidden="true"></div>' +
      "<p>Thank you for taking the time to complete the Mobility Reliability Assessment.</p>" +
      "<p><strong>Your consultation request has been received.</strong> A member of the Strike team will review your results and contact you shortly to discuss your assessment and how we can support your business.</p>",
      "Done"
    );
  }

  function showConsultationErrorDialog(err) {
    var detail = err && err.message ? escapeHtml(err.message) : "Please try again in a moment.";
    showConsultationDialog(
      "error",
      '<h2 id="consultationDialogTitle">Unable to submit consultation request</h2>' +
        "<p>We could not complete your consultation request right now.</p>" +
        "<p>" + detail + "</p>" +
        "<p>Please try again, or contact Strike directly if the problem continues.</p>",
      "Close"
    );
  }

  function setConsultationButtonState(root, disabled, label) {
    var btn = (root || document).querySelector("#btnBookConsultation");
    if (!btn) return;
    btn.disabled = !!disabled;
    if (label) btn.textContent = label;
  }

  function onBookConsultationClick(e) {
    if (e) e.preventDefault();

    var email = normalizeEmail((state.lead && state.lead.email) || readPersistedLeadEmail());
    var btnRoot = els.fullReportMount || document;
    var assessmentId = state.assessmentId;

    if (consultationInFlight) return;

    if (hasConsultationBeenSubmitted(assessmentId)) {
      showConsultationSuccessDialog();
      setConsultationButtonState(btnRoot, true, "Consultation Requested");
      return;
    }

    if (!email) {
      showConsultationErrorDialog(new Error("Your email was not found. Please refresh and complete the form again."));
      return;
    }

    consultationInFlight = true;
    setConsultationButtonState(btnRoot, true, "Submitting…");

    var payload = buildConsultationPayload();
    payload.email = email;
    payload.lead.email = email;

    postConsultationToWebhook(payload)
      .then(function () {
        markConsultationSubmitted(assessmentId);
        setConsultationButtonState(btnRoot, true, "Consultation Requested");
        showConsultationSuccessDialog();
      })
      .catch(function (err) {
        setConsultationButtonState(btnRoot, false, "Book Consultation");
        showConsultationErrorDialog(err || new Error("Request failed"));
      })
      .then(function () {
        consultationInFlight = false;
      });
  }

  /**
   * Complete assessment payload for Apps Script / Klaviyo / Sheets sync.
   * Generated in buildAssessmentPayload(); also stored on state.lastAssessmentPayload
   * and localStorage under ASSESSMENT_PAYLOAD_KEY after lead capture.
   */
  function buildAssessmentPayload() {
    ensureReportBuilt();

    if (!state.assessmentId) {
      state.assessmentId = generateAssessmentId();
    }

    var lead = mergeLead(state.lead || {});
    state.lead = lead;

    var report = state.report;
    var payload = {
      assessmentId: state.assessmentId,
      completedAt: (report && report.answeredAt) || new Date().toISOString(),
      capturedAt: new Date().toISOString(),
      lead: {
        email: lead.email || "",
        firstName: lead.firstName || "",
        lastName: lead.lastName || "",
        company: lead.company || "",
        phone: lead.phone || "",
        source: lead.source || "klaviyo",
        formId: lead.formId || KLAVIYO_FORM_ID
      },
      answers: JSON.parse(JSON.stringify(state.answers || {})),
      metrics: JSON.parse(JSON.stringify(state.metrics || {})),
      report: report
        ? {
            score: report.score,
            risk: {
              id: report.risk && report.risk.id,
              label: report.risk && report.risk.label,
              summary: report.risk && report.risk.summary
            },
            findings: (report.findings || []).map(function (f) {
              return {
                text: f.text,
                severity: f.severity,
                sectionId: f.sectionId,
                tags: f.tags || []
              };
            }),
            recommendationCopy: (report.recommendationCopy || []).slice(),
            financials: {
              annualProductivityLoss: report.financials.annualProductivityLoss,
              avoidableDeviceCosts: report.financials.avoidableDeviceCosts,
              annualSupportCost: report.financials.annualSupportCost,
              totalExposure: report.financials.totalExposure
            },
            sectionAverages: JSON.parse(JSON.stringify(report.sectionAverages || {})),
            answeredAt: report.answeredAt
          }
        : null,
      meta: {
        pageUrl: typeof location !== "undefined" ? location.href : "",
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        reportUnlocked: !!state.reportUnlocked
      }
    };

    state.lastAssessmentPayload = payload;
    try {
      localStorage.setItem(ASSESSMENT_PAYLOAD_KEY, JSON.stringify(payload));
    } catch (e) { /* ignore */ }

    return payload;
  }

  function saveState() {
    try {
      var payload = {
        questionIndex: state.questionIndex,
        answers: state.answers,
        metrics: state.metrics,
        lead: state.lead,
        reportUnlocked: state.reportUnlocked,
        assessmentId: state.assessmentId,
        step: state.step === "calculating" ? "metrics" : state.step
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      if (state.lead && state.lead.email) {
        persistLeadEmail(state.lead.email);
      }
    } catch (e) { /* ignore */ }
  }

  function loadState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var data = JSON.parse(raw);
      if (data.answers) state.answers = data.answers;
      if (data.metrics) state.metrics = Object.assign(state.metrics, data.metrics);
      if (typeof data.questionIndex === "number") state.questionIndex = data.questionIndex;
      if (data.lead) state.lead = data.lead;
      if (data.reportUnlocked) state.reportUnlocked = true;
      if (data.assessmentId) state.assessmentId = data.assessmentId;
      if (data.step === "question" || data.step === "metrics") {
        state.step = data.step;
      }

      /* Always re-attach persisted email if lead email was lost */
      var persistedEmail = readPersistedLeadEmail();
      if (persistedEmail) {
        state.lead = mergeLead(state.lead || {});
      }
    } catch (e) { /* ignore */ }
  }

  /* ═══════════════════════════════════════════════════════════
     MODULE: UI — DOM cache & helpers
     ═══════════════════════════════════════════════════════════ */

  var $ = function (id) {
    return document.getElementById(id);
  };

  var els = {};

  function cacheElements() {
    els.app = $("assessmentApp");
    els.screenWizard = $("screenWizard");
    els.screenCalculating = $("screenCalculating");
    els.screenResults = $("screenResults");
    els.sidebarNav = $("sidebarNav");
    els.sidebarProgressLabel = $("sidebarProgressLabel");
    els.topProgressLabel = $("topProgressLabel");
    els.topProgressPct = $("topProgressPct");
    els.mobileProgressPct = $("mobileProgressPct");
    els.progressBar = $("progressBar");
    els.progressBarFill = $("progressBarFill");
    els.mobileProgressBar = $("mobileProgressBar");
    els.mobileProgressFill = $("mobileProgressFill");
    els.panelQuestion = $("panelQuestion");
    els.panelMetrics = $("panelMetrics");
    els.questionPanel = $("questionPanel");
    els.questionSectionLabel = $("questionSectionLabel");
    els.questionCounter = $("questionCounter");
    els.questionTitle = $("questionTitle");
    els.ratingOptions = $("ratingOptions");
    els.metricsForm = $("metricsForm");
    els.btnBack = $("btnBack");
    els.btnNext = $("btnNext");
    els.calcProgressFill = $("calcProgressFill");
    els.calcProgressPct = $("calcProgressPct");
    els.calcProgressBar = $("calcProgressBar");
    els.calcSteps = $("calcSteps");
    els.scoreGaugeValue = $("scoreGaugeValue");
    els.scoreNumber = $("scoreNumber");
    els.riskBadges = $("riskBadges");
    els.riskSummary = $("riskSummary");
    els.metricProductivity = $("metricProductivity");
    els.metricDeviceCosts = $("metricDeviceCosts");
    els.metricRiskLevel = $("metricRiskLevel");
    els.metricHealthScore = $("metricHealthScore");
    els.metricSupportCost = $("metricSupportCost");
    els.metricTotalExposure = $("metricTotalExposure");
    els.findingsList = $("findingsList");
    els.roadmapGrid = $("roadmapGrid");
    els.recommendationsList = $("recommendationsList");
    els.btnDownload = $("btnDownload");
    els.reportGate = $("reportGate");
    els.fullReportMount = $("fullReportMount");
    els.reportPlaceholder = $("reportPlaceholder");
    els.leadOverlay = $("leadOverlay");
    els.btnRetake = $("btnRetake");
    els.resultsRetake = $("resultsRetake");
    els.klaviyoForm = $("klaviyo-enterprise-form");
    els.klaviyoEmbed = $("klaviyoEmbed");
  }

  function formatCurrency(n) {
    return "$" + Math.round(n).toLocaleString("en-AU");
  }

  function getSectionLabel(sectionId) {
    for (var i = 0; i < SECTIONS.length; i++) {
      if (SECTIONS[i].id === sectionId) {
        var s = SECTIONS[i];
        if (s.number) return "Section " + s.number + " – " + s.label;
        return s.label;
      }
    }
    return "";
  }

  function getSectionEyebrow(sectionId) {
    for (var i = 0; i < SECTIONS.length; i++) {
      if (SECTIONS[i].id === sectionId) {
        return SECTIONS[i].label.toUpperCase();
      }
    }
    return "";
  }

  function getActiveSectionId() {
    if (state.step === "metrics" || state.step === "calculating") return "operational-metrics";
    if (state.step === "results") return "results";
    if (state.step === "question") return QUESTIONS[state.questionIndex].sectionId;
    return SECTIONS[0].id;
  }

  function getCompletedSectionIds() {
    var completed = {};
    var sectionQuestionCounts = {};
    var sectionAnswered = {};

    QUESTIONS.forEach(function (q) {
      sectionQuestionCounts[q.sectionId] = (sectionQuestionCounts[q.sectionId] || 0) + 1;
      if (state.answers[q.id] != null) {
        sectionAnswered[q.sectionId] = (sectionAnswered[q.sectionId] || 0) + 1;
      }
    });

    Object.keys(sectionQuestionCounts).forEach(function (id) {
      if (sectionAnswered[id] === sectionQuestionCounts[id]) {
        completed[id] = true;
      }
    });

    if (state.step === "calculating" || state.step === "results") {
      completed["operational-metrics"] = true;
    }
    if (state.step === "results") {
      completed.results = true;
    }

    return completed;
  }

  function getSectionOrderIndex(sectionId) {
    for (var i = 0; i < SECTIONS.length; i++) {
      if (SECTIONS[i].id === sectionId) return i;
    }
    return -1;
  }

  /* Account for Shopify sticky header so content isn't flush under the nav */
  var SCROLL_OFFSET = 96;

  function scrollToElementWithOffset(el, offset) {
    if (!el) return;
    var topOffset = typeof offset === "number" ? offset : SCROLL_OFFSET;
    var rect = el.getBoundingClientRect();
    var top = rect.top + (window.pageYOffset || document.documentElement.scrollTop) - topOffset;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }

  function showScreen(screenEl, options) {
    options = options || {};
    var scrollMode = options.scrollMode || "none"; /* none | top | element */

    [els.screenWizard, els.screenCalculating, els.screenResults].forEach(function (s) {
      if (!s) return;
      s.classList.remove("is-active");
      s.hidden = true;
    });
    screenEl.hidden = false;
    screenEl.removeAttribute("hidden");
    screenEl.classList.add("is-active");

    if (scrollMode === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (scrollMode === "element") {
      var target = options.scrollTarget || screenEl;
      setTimeout(function () {
        scrollToElementWithOffset(target, options.scrollOffset);
      }, 40);
    }
  }

  /* ── Sidebar & Progress ─────────────────────────────────── */

  function renderSidebar() {
    var activeId = getActiveSectionId();
    var completed = getCompletedSectionIds();
    var activeIndex = getSectionOrderIndex(activeId);
    var html = "";

    SECTIONS.forEach(function (s, index) {
      var classes = ["sidebar__item"];
      var isActive = s.id === activeId;
      var isComplete = !!completed[s.id] && (!isActive || state.step === "results");
      var isFuture = index > activeIndex && !completed[s.id];

      if (isActive) classes.push("is-active");
      if (isComplete) classes.push("is-complete");
      if (isFuture) classes.push("is-future");

      var markerContent = isComplete
        ? "✓"
        : (s.number != null ? String(s.number) : (s.type === "metrics" ? "◇" : "★"));

      /* Short labels — number lives in the marker circle */
      var navLabel = s.id === "results" ? "Results" : s.label;

      html +=
        '<div class="' + classes.join(" ") + '" data-section="' + s.id + '">' +
          '<span class="sidebar__marker" aria-hidden="true">' + markerContent + "</span>" +
          '<span class="sidebar__label">' + navLabel + "</span>" +
        "</div>";
    });

    els.sidebarNav.innerHTML = html;
  }

  function getOverallProgress() {
    var totalUnits = QUESTIONS.length + 1;
    var done = 0;

    if (state.step === "calculating" || state.step === "results") {
      done = totalUnits;
    } else if (state.step === "metrics") {
      done = QUESTIONS.length;
    } else {
      done = state.questionIndex;
      if (state.answers[QUESTIONS[state.questionIndex].id] != null) {
        done = state.questionIndex + 1;
      }
    }

    var pct = Math.round((done / totalUnits) * 100);
    return { done: done, total: totalUnits, pct: Math.min(100, pct) };
  }

  function renderTopProgress() {
    var progress = getOverallProgress();
    var label;

    if (state.step === "metrics") {
      label = "Operational Metrics";
    } else if (state.step === "question") {
      label = "Question " + (state.questionIndex + 1) + " of " + QUESTIONS.length;
    } else {
      label = "Assessment";
    }

    var pctText = progress.pct + "% Complete";

    if (els.sidebarProgressLabel) els.sidebarProgressLabel.textContent = label;
    if (els.topProgressLabel) els.topProgressLabel.textContent = label;
    if (els.topProgressPct) els.topProgressPct.textContent = pctText;
    if (els.mobileProgressPct) els.mobileProgressPct.textContent = pctText;

    if (els.progressBarFill) {
      els.progressBarFill.style.width = progress.pct + "%";
      els.progressBarFill.style.setProperty("width", progress.pct + "%", "important");
    }
    if (els.progressBar) els.progressBar.setAttribute("aria-valuenow", String(progress.pct));
    if (els.mobileProgressFill) {
      els.mobileProgressFill.style.width = progress.pct + "%";
      els.mobileProgressFill.style.setProperty("width", progress.pct + "%", "important");
    }
    if (els.mobileProgressBar) els.mobileProgressBar.setAttribute("aria-valuenow", String(progress.pct));
  }

  /* ── Questions UI ───────────────────────────────────────── */

  function renderQuestion(animate) {
    var q = QUESTIONS[state.questionIndex];
    var selected = state.answers[q.id];
    var qNum = state.questionIndex + 1;

    els.questionSectionLabel.textContent = getSectionEyebrow(q.sectionId);
    els.questionCounter.textContent = "Question " + qNum + " of " + QUESTIONS.length;
    els.questionTitle.textContent = q.text;

    var html = "";
    RATING_OPTIONS.forEach(function (opt) {
      var isSelected = selected === opt.value;
      html +=
        '<button type="button" class="rating-option' + (isSelected ? " is-selected" : "") + '"' +
        ' role="radio" aria-checked="' + (isSelected ? "true" : "false") + '"' +
        ' data-value="' + opt.value + '">' +
          '<span class="rating-option__check" aria-hidden="true">✓</span>' +
          '<span class="rating-option__label">' + opt.label + "</span>" +
        "</button>";
    });

    els.ratingOptions.innerHTML = html;

    Array.prototype.forEach.call(els.ratingOptions.querySelectorAll(".rating-option"), function (btn) {
      btn.addEventListener("click", onRatingSelect);
    });

    els.panelQuestion.removeAttribute("hidden");
    els.panelQuestion.hidden = false;
    els.panelQuestion.classList.add("is-active");
    els.panelMetrics.hidden = true;
    els.panelMetrics.classList.remove("is-active");

    if (animate !== false) {
      els.questionPanel.classList.remove("anim-enter-left", "anim-enter-right");
      void els.questionPanel.offsetWidth;
      els.questionPanel.classList.add(
        state.direction === "forward" ? "anim-enter-right" : "anim-enter-left"
      );
    }

    updateNavButtons();
    renderSidebar();
    renderTopProgress();
  }

  function onRatingSelect(e) {
    var value = Number(e.currentTarget.getAttribute("data-value"));
    var q = QUESTIONS[state.questionIndex];

    state.answers[q.id] = value;
    saveState();

    Array.prototype.forEach.call(els.ratingOptions.querySelectorAll(".rating-option"), function (el) {
      var selected = Number(el.getAttribute("data-value")) === value;
      el.classList.toggle("is-selected", selected);
      el.setAttribute("aria-checked", selected ? "true" : "false");
    });

    updateNavButtons();
    renderTopProgress();
    renderSidebar();
  }

  /* ── Metrics UI ─────────────────────────────────────────── */

  function renderMetrics(animate) {
    els.panelQuestion.hidden = true;
    els.panelQuestion.classList.remove("is-active");
    els.panelMetrics.removeAttribute("hidden");
    els.panelMetrics.hidden = false;
    els.panelMetrics.classList.add("is-active");

    $("metricWorkers").value = state.metrics.workers || "";
    $("metricHourlyCost").value = state.metrics.hourlyCost || "";
    $("metricDowntime").value = state.metrics.downtime || "";
    $("metricDamaged").value = state.metrics.damaged || "";
    $("metricReplaceCost").value = state.metrics.replaceCost || "";
    $("metricTickets").value = state.metrics.tickets || "";

    if (animate !== false) {
      var inner = $("metricsPanel") || els.panelMetrics.querySelector(".assessment-step__inner");
      if (inner) {
        inner.classList.remove("anim-enter-left", "anim-enter-right");
        void inner.offsetWidth;
        inner.classList.add(
          state.direction === "forward" ? "anim-enter-right" : "anim-enter-left"
        );
      }
    }

    updateNavButtons();
    renderSidebar();
    renderTopProgress();
  }

  function readMetricsFromForm() {
    state.metrics = {
      workers: $("metricWorkers").value,
      hourlyCost: $("metricHourlyCost").value,
      downtime: $("metricDowntime").value,
      damaged: $("metricDamaged").value,
      replaceCost: $("metricReplaceCost").value,
      tickets: $("metricTickets").value
    };
    saveState();
  }

  function isMetricsValid() {
    var m = state.metrics;
    return ["workers", "hourlyCost", "downtime", "damaged", "replaceCost", "tickets"].every(function (key) {
      return m[key] !== "" && m[key] != null && !isNaN(Number(m[key]));
    });
  }

  function validateMetricsUI() {
    var map = {
      workers: "metricWorkers",
      hourlyCost: "metricHourlyCost",
      downtime: "metricDowntime",
      damaged: "metricDamaged",
      replaceCost: "metricReplaceCost",
      tickets: "metricTickets"
    };
    var valid = true;
    Object.keys(map).forEach(function (key) {
      var el = $(map[key]);
      var ok = state.metrics[key] !== "" && state.metrics[key] != null && !isNaN(Number(state.metrics[key]));
      el.classList.toggle("is-invalid", !ok);
      if (!ok) valid = false;
    });
    return valid;
  }

  /* ═══════════════════════════════════════════════════════════
     MODULE: Navigation
     ═══════════════════════════════════════════════════════════ */

  function updateNavButtons() {
    var canGoBack = !(state.step === "question" && state.questionIndex === 0);
    els.btnBack.disabled = !canGoBack;

    if (state.step === "question") {
      var q = QUESTIONS[state.questionIndex];
      els.btnNext.disabled = state.answers[q.id] == null;
      els.btnNext.innerHTML =
        'Next <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M3.75 9h10.5M9.75 4.5L14.25 9l-4.5 4.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    } else if (state.step === "metrics") {
      els.btnNext.disabled = !isMetricsValid();
      els.btnNext.innerHTML =
        'See Results <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M3.75 9h10.5M9.75 4.5L14.25 9l-4.5 4.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }
  }

  function startAssessment() {
    if (typeof state.questionIndex !== "number" || state.questionIndex < 0) {
      state.questionIndex = 0;
    }
    state.direction = "forward";
    showScreen(els.screenWizard, { scrollMode: "none" });

    if (state.step === "metrics") {
      renderMetrics(false);
    } else {
      state.step = "question";
      renderQuestion(false);
    }
  }

  function goNext() {
    state.direction = "forward";

    if (state.step === "question") {
      var q = QUESTIONS[state.questionIndex];
      if (state.answers[q.id] == null) return;

      if (state.questionIndex < QUESTIONS.length - 1) {
        state.questionIndex += 1;
        renderQuestion();
        saveState();
        return;
      }

      state.step = "metrics";
      renderMetrics();
      saveState();
      return;
    }

    if (state.step === "metrics") {
      readMetricsFromForm();
      if (!validateMetricsUI()) {
        updateNavButtons();
        return;
      }
      beginCalculating();
    }
  }

  function goBack() {
    state.direction = "back";

    if (state.step === "metrics") {
      state.step = "question";
      state.questionIndex = QUESTIONS.length - 1;
      showScreen(els.screenWizard, { scrollMode: "none" });
      renderQuestion();
      saveState();
      return;
    }

    if (state.step === "question") {
      if (state.questionIndex === 0) return;
      state.questionIndex -= 1;
      renderQuestion();
      saveState();
    }
  }

  /* ═══════════════════════════════════════════════════════════
     MODULE: Calculating
     ═══════════════════════════════════════════════════════════ */

  function beginCalculating() {
    state.step = "calculating";
    showScreen(els.screenCalculating, {
      scrollMode: "element",
      scrollTarget: els.app || els.screenCalculating,
      scrollOffset: SCROLL_OFFSET
    });
    renderSidebar();

    var milestones = [0, 25, 50, 75, 100];
    var stepEls = els.calcSteps.querySelectorAll("li");
    var i = 0;

    els.calcProgressFill.style.setProperty("width", "0%", "important");
    els.calcProgressPct.textContent = "0%";
    els.calcProgressBar.setAttribute("aria-valuenow", "0");

    Array.prototype.forEach.call(stepEls, function (li) {
      li.classList.remove("is-active", "is-done");
    });

    function tick() {
      if (i >= milestones.length) {
        state.report = ScoringEngine.buildReport(state.answers, state.metrics);
        setTimeout(showResults, 350);
        return;
      }

      var pct = milestones[i];
      els.calcProgressFill.style.setProperty("width", pct + "%", "important");
      els.calcProgressPct.textContent = pct + "%";
      els.calcProgressBar.setAttribute("aria-valuenow", String(pct));

      Array.prototype.forEach.call(stepEls, function (li) {
        var at = Number(li.getAttribute("data-at"));
        li.classList.toggle("is-done", at < pct || (pct === 100 && at === 100));
        li.classList.toggle("is-active", at === pct && pct < 100);
      });

      i += 1;
      setTimeout(tick, 480);
    }

    setTimeout(tick, 200);
  }

  /* ═══════════════════════════════════════════════════════════
     MODULE: Results
     Full report HTML is NOT in the DOM until lead unlock.
     ═══════════════════════════════════════════════════════════ */

  function showResults() {
    state.step = "results";

    /* Scroll to the report title so score + risk stay visible above the form */
    var header = els.screenResults && els.screenResults.querySelector(".results__header");
    showScreen(els.screenResults, {
      scrollMode: "element",
      scrollTarget: header || els.app || els.screenResults,
      scrollOffset: SCROLL_OFFSET
    });
    renderSidebar();
    renderResultsPreview(state.report);

    if (state.reportUnlocked && state.report) {
      unlockFullReport(false);
    } else {
      state.reportUnlocked = false;
      lockFullReport();
    }

    saveState();
  }

  function animateScore(target) {
    var circumference = 2 * Math.PI * 86;
    var offset = circumference - (target / 100) * circumference;
    els.scoreGaugeValue.style.strokeDasharray = String(circumference);
    els.scoreGaugeValue.style.strokeDashoffset = String(circumference);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        els.scoreGaugeValue.style.strokeDashoffset = String(offset);
      });
    });

    var duration = 1100;
    var start = null;

    function frame(ts) {
      if (!start) start = ts;
      var t = Math.min(1, (ts - start) / duration);
      var eased = 1 - Math.pow(1 - t, 3);
      els.scoreNumber.textContent = String(Math.round(eased * target));
      if (t < 1) requestAnimationFrame(frame);
    }

    requestAnimationFrame(frame);
  }

  /** Preview only — never writes full-report details into the locked area. */
  function renderResultsPreview(report) {
    if (!report) return;

    animateScore(report.score);

    if (els.riskBadges) {
      Array.prototype.forEach.call(els.riskBadges.querySelectorAll(".risk-badge"), function (badge) {
        badge.classList.toggle("is-active", badge.getAttribute("data-level") === report.risk.id);
      });
    }

    var riskPanel = $("riskPanel");
    var riskMeter = $("riskMeter");
    var riskLevelLabel = $("riskLevelLabel");
    if (riskPanel) riskPanel.setAttribute("data-level", report.risk.id);
    if (riskMeter) riskMeter.setAttribute("data-level", report.risk.id);
    if (riskLevelLabel) riskLevelLabel.textContent = report.risk.label;

    if (els.riskSummary) els.riskSummary.textContent = report.risk.summary;
    if (els.metricProductivity) {
      els.metricProductivity.textContent = formatCurrency(report.financials.annualProductivityLoss);
    }
    if (els.metricDeviceCosts) {
      els.metricDeviceCosts.textContent = formatCurrency(report.financials.avoidableDeviceCosts);
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function buildFullReportHtml(report) {
    if (!report) return "";

    var findingsHtml = report.findings
      .map(function (f) { return "<li>" + escapeHtml(f.text) + "</li>"; })
      .join("");

    var roadmapHtml = RECOMMENDATIONS.map(function (rec) {
      return (
        '<article class="roadmap-card">' +
          '<div class="roadmap-card__icon" aria-hidden="true">' + rec.icon + "</div>" +
          '<h3 class="roadmap-card__title">' + escapeHtml(rec.title) + "</h3>" +
          '<p class="roadmap-card__text">' + escapeHtml(rec.text) + "</p>" +
          '<a class="roadmap-card__cta" href="' + escapeHtml(rec.href) + '" target="_blank" rel="noopener noreferrer">' +
            escapeHtml(rec.cta) +
            ' <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2.5 7h9M7.5 3.5L11 7l-3.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          "</a>" +
        "</article>"
      );
    }).join("");

    var recommendationsHtml = (report.recommendationCopy || [])
      .map(function (line) { return "<li>" + escapeHtml(line) + "</li>"; })
      .join("");

    return (
      '<div class="report-download-bar no-print-hide">' +
        '<div class="report-download-bar__text">' +
          "Your full report is unlocked" +
          "<span>Download a PDF copy for your records or share it with your team.</span>" +
        "</div>" +
        '<button type="button" class="btn btn--primary btn--lg" id="btnDownloadPdf">Download PDF Report</button>' +
      "</div>" +

      '<section class="results__section" aria-labelledby="execSummaryHeading">' +
        '<h2 class="results__section-title" id="execSummaryHeading">Executive Summary</h2>' +
        '<div class="metric-cards">' +
          '<article class="metric-card"><p class="metric-card__label">Estimated Risk Level</p>' +
            '<p class="metric-card__value" data-risk="' + escapeHtml(report.risk.id) + '">' + escapeHtml(report.risk.label) + "</p></article>" +
          '<article class="metric-card"><p class="metric-card__label">Overall Health Score</p>' +
            '<p class="metric-card__value">' + report.score + " / 100</p></article>" +
          '<article class="metric-card"><p class="metric-card__label">Estimated Annual Support Cost</p>' +
            '<p class="metric-card__value">' + formatCurrency(report.financials.annualSupportCost) + "</p></article>" +
          '<article class="metric-card"><p class="metric-card__label">Total Estimated Exposure</p>' +
            '<p class="metric-card__value">' + formatCurrency(report.financials.totalExposure) + "</p></article>" +
        "</div>" +
      "</section>" +

      '<section class="results__section" aria-labelledby="findingsHeading">' +
        '<h2 class="results__section-title" id="findingsHeading">Key Findings</h2>' +
        '<ul class="findings-list">' + findingsHtml + "</ul>" +
      "</section>" +

      '<section class="results__section" aria-labelledby="roadmapHeading">' +
        '<h2 class="results__section-title" id="roadmapHeading">Device Modernisation Roadmap</h2>' +
        '<p class="results__section-lede">Six priority workstreams to strengthen reliability, reduce cost, and improve governance.</p>' +
        '<div class="roadmap-grid">' + roadmapHtml + "</div>" +
      "</section>" +

      '<section class="results__section" aria-labelledby="recommendationsHeading">' +
        '<h2 class="results__section-title" id="recommendationsHeading">Recommendations</h2>' +
        '<ul class="recommendations-list">' + recommendationsHtml + "</ul>" +
      "</section>" +

      '<aside class="cta-card" id="fullReportCta">' +
        '<div class="cta-card__content">' +
          '<h2 class="cta-card__title">Ready to improve your Enterprise Mobility Strategy?</h2>' +
          '<p class="cta-card__text">Book a consultation with Strike\'s Enterprise Mobility Specialists.</p>' +
        "</div>" +
        '<div class="cta-card__actions">' +
          '<button type="button" class="btn btn--primary btn--lg" id="btnBookConsultation">Book Consultation</button>' +
        "</div>" +
      "</aside>" +

      '<aside class="report-pdf-cta no-print-hide" id="reportPdfCta">' +
        '<div class="report-pdf-cta__text">' +
          "Download your Mobility Reliability Report" +
          "<span>Save a PDF copy for stakeholders, procurement reviews, and internal planning.</span>" +
        "</div>" +
        '<button type="button" class="btn btn--lg" id="btnDownloadPdfSecondary">Download PDF Report</button>' +
      "</aside>"
    );
  }

  function sizeReportGateToForm() {
    if (!els.reportGate || state.reportUnlocked) return;
    var card = els.klaviyoForm || (els.leadOverlay && els.leadOverlay.querySelector(".lead-card"));
    if (!card) return;
    var needed = Math.ceil(card.getBoundingClientRect().height) + 56;
    els.reportGate.style.minHeight = Math.max(needed, 420) + "px";
  }

  function setRetakeVisible(visible) {
    if (!els.resultsRetake) return;
    if (visible) {
      els.resultsRetake.hidden = false;
      els.resultsRetake.removeAttribute("hidden");
    } else {
      els.resultsRetake.hidden = true;
      els.resultsRetake.setAttribute("hidden", "");
    }
  }

  function lockFullReport() {
    state.reportUnlocked = false;
    els.reportGate.classList.remove("is-unlocked");
    if (els.app) els.app.classList.remove("is-report-unlocked");
    if (els.fullReportMount) {
      /* Keep real report out of the DOM while locked */
      els.fullReportMount.innerHTML = "";
      els.fullReportMount.hidden = true;
    }
    if (els.reportPlaceholder) {
      els.reportPlaceholder.hidden = false;
      els.reportPlaceholder.setAttribute("aria-hidden", "true");
    }
    if (els.leadOverlay) {
      els.leadOverlay.hidden = false;
      els.leadOverlay.classList.remove("is-hidden");
      els.leadOverlay.style.display = "";
    }
    setRetakeVisible(false);
    setTimeout(sizeReportGateToForm, 50);
    setTimeout(sizeReportGateToForm, 400);
    setTimeout(sizeReportGateToForm, 1200);
  }

  function unlockFullReport(scrollToReport) {
    if (!state.report) return;

    state.reportUnlocked = true;
    els.reportGate.classList.add("is-unlocked");
    if (els.app) els.app.classList.add("is-report-unlocked");
    if (els.reportGate) els.reportGate.style.minHeight = "";

    if (els.reportPlaceholder) {
      els.reportPlaceholder.hidden = true;
    }
    if (els.leadOverlay) {
      els.leadOverlay.hidden = true;
      els.leadOverlay.classList.add("is-hidden");
      els.leadOverlay.style.display = "none";
    }
    if (els.fullReportMount) {
      els.fullReportMount.hidden = false;
      els.fullReportMount.innerHTML = buildFullReportHtml(state.report);
      bindReportDownloadButtons(els.fullReportMount);
    }
    setRetakeVisible(true);

    saveState();

    if (scrollToReport !== false && els.fullReportMount) {
      setTimeout(function () {
        els.fullReportMount.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    }
  }

  function retakeAssessment() {
    state.answers = {};
    state.metrics = {
      workers: "",
      hourlyCost: "",
      downtime: "",
      damaged: "",
      replaceCost: "",
      tickets: ""
    };
    state.questionIndex = 0;
    state.report = null;
    state.lead = null;
    state.reportUnlocked = false;
    state.assessmentId = null;
    state.lastAssessmentPayload = null;
    state.step = "question";
    state.direction = "forward";

    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(ASSESSMENT_PAYLOAD_KEY);
    } catch (e) { /* ignore */ }
    clearPersistedLeadEmail();

    lockFullReport();
    showScreen(els.screenWizard, {
      scrollMode: "element",
      scrollTarget: els.app || els.screenWizard,
      scrollOffset: SCROLL_OFFSET
    });
    renderQuestion(false);
    saveState();
  }

  function bindReportDownloadButtons(root) {
    Array.prototype.forEach.call(root.querySelectorAll("#btnDownloadPdf, #btnDownloadPdfSecondary"), function (btn) {
      btn.addEventListener("click", downloadPdfReport);
    });
    var consultBtn = root.querySelector("#btnBookConsultation");
    if (consultBtn) {
      consultBtn.addEventListener("click", onBookConsultationClick);
    }
  }

  function getPdfAssets() {
    var custom = window.StrikeMobilityAssets || {};
    return {
      logo: custom.logoIcon || custom.logoWhite || custom.logo ||
        "https://cdn.shopify.com/s/files/1/0667/4962/8635/files/strike-logo-HD-white-200x_22ca2880-7368-48a3-8af6-e08b754c9567.png?v=1683075712",
      banner: custom.banner ||
        "https://cdn.shopify.com/s/files/1/0667/4962/8635/files/enterprise.png"
    };
  }

  function formatReportDate(iso) {
    try {
      return new Date(iso || Date.now()).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
    } catch (e) {
      return new Date().toLocaleDateString();
    }
  }

  /** PDF layout mirrors the on-page unlocked results (simpler + consistent). */
  function buildPdfDocumentHtml(report) {
    var assets = getPdfAssets();
    var lead = state.lead || {};
    var company = lead.company || lead.organization || "";
    var name = [lead.firstName, lead.lastName].filter(Boolean).join(" ");
    var preparedFor = name && company ? name + " · " + company : (name || company || "Your Organisation");
    var dateLabel = formatReportDate(report.answeredAt);

    var findingsHtml = (report.findings || []).map(function (f) {
      return "<li>" + escapeHtml(f.text) + "</li>";
    }).join("");

    var roadmapHtml = RECOMMENDATIONS.map(function (rec) {
      return (
        '<article class="roadmap-card">' +
          '<h3>' + escapeHtml(rec.title) + "</h3>" +
          "<p>" + escapeHtml(rec.text) + "</p>" +
        "</article>"
      );
    }).join("");

    var recommendationsHtml = (report.recommendationCopy || []).map(function (line) {
      return "<li>" + escapeHtml(line) + "</li>";
    }).join("");

    return (
      "<!DOCTYPE html><html><head><meta charset='UTF-8'>" +
      "<title>Strike</title>" +
      "<style>" +
      "@page{size:A4;margin:12mm}" +
      "*{box-sizing:border-box;margin:0;padding:0}" +
      "body{font-family:'PT Sans',Arial,Tahoma,sans-serif;color:#222;background:#fff;font-size:12px;line-height:1.45}" +
      ".page{max-width:180mm;margin:0 auto}" +
      ".hero{position:relative;border-radius:10px;overflow:hidden;margin-bottom:18px;background:#111}" +
      ".hero-banner{display:block;width:100%;height:170px;object-fit:cover;object-position:center}" +
      ".hero-bar{position:absolute;left:0;right:0;bottom:0;background:rgba(0,0,0,.55);color:#fff;padding:14px 16px 16px}" +
      ".hero-bar-top{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:10px}" +
      ".logo{height:28px;width:auto;display:block}" +
      ".hero-bar h1{font-size:20px;font-weight:700;letter-spacing:-0.02em;line-height:1.2;margin:0 0 4px}" +
      ".hero-bar .meta{font-size:11px;opacity:.92;margin:0}" +
      ".eyebrow{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#3478B8;margin-bottom:4px}" +
      ".title{font-size:20px;font-weight:700;color:#222;margin-bottom:12px}" +
      ".preview{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:16px}" +
      ".card{border:1px solid #E4E7EB;border-radius:10px;padding:12px 14px;background:#fff}" +
      ".card .label{font-size:10px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#6B7280;margin-bottom:4px}" +
      ".card .value{font-size:20px;font-weight:700;color:#222;line-height:1.15}" +
      ".card .value[data-risk='moderate']{color:#C47A12}" +
      ".card .value[data-risk='high'],.card .value[data-risk='critical']{color:#C0392B}" +
      ".card .value[data-risk='low']{color:#2E7D4F}" +
      ".card .note{font-size:11px;color:#6B7280;margin-top:6px;line-height:1.4}" +
      ".section{margin:0 0 16px;page-break-inside:avoid;break-inside:avoid}" +
      ".section h2{font-size:16px;color:#222;margin-bottom:8px}" +
      ".section .lede{font-size:11px;color:#6B7280;margin-bottom:8px}" +
      ".metrics{display:grid;grid-template-columns:1fr 1fr;gap:8px}" +
      ".findings,.recs{list-style:none}" +
      ".findings li,.recs li{border:1px solid #E4E7EB;border-radius:8px;padding:9px 12px;margin-bottom:6px;font-size:12px}" +
      ".findings li::before{content:'';display:inline-block;width:7px;height:7px;border-radius:50%;background:#3478B8;margin-right:8px;vertical-align:middle}" +
      ".roadmap{display:grid;grid-template-columns:1fr 1fr;gap:8px}" +
      ".roadmap-card{border:1px solid #E4E7EB;border-radius:8px;padding:10px 12px}" +
      ".roadmap-card h3{font-size:13px;margin-bottom:4px;color:#222}" +
      ".roadmap-card p{font-size:11px;color:#5B6475}" +
      ".cta{background:#3478B8;color:#fff;border-radius:10px;padding:14px 16px;display:flex;justify-content:space-between;align-items:center;gap:12px}" +
      ".cta h2{font-size:15px;margin-bottom:4px;color:#fff}" +
      ".cta p{font-size:11px;opacity:.92}" +
      ".cta a{display:inline-block;background:#fff;color:#3478B8;text-decoration:none;font-weight:700;padding:9px 14px;border-radius:6px;font-size:11px;white-space:nowrap}" +
      ".footer{margin-top:14px;padding-top:10px;border-top:1px solid #E4E7EB;font-size:10px;color:#6B7280;display:flex;justify-content:space-between}" +
      "@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}.page{max-width:none}}" +
      "</style></head><body><div class='page'>" +

      '<header class="hero">' +
        (assets.banner
          ? '<img class="hero-banner" src="' + assets.banner + '" alt="" />'
          : "") +
        '<div class="hero-bar">' +
          '<div class="hero-bar-top">' +
            (assets.logo
              ? '<img class="logo" src="' + assets.logo + '" alt="Strike" />'
              : "<strong>Strike</strong>") +
          "</div>" +
          "<h1>Your Mobility Reliability Report</h1>" +
          '<p class="meta">Prepared for ' + escapeHtml(preparedFor) + " · " + escapeHtml(dateLabel) + "</p>" +
        "</div>" +
      "</header>" +

      '<p class="eyebrow">Strike Enterprise Mobility Diagnostic</p>' +
      '<p class="title">Assessment Results</p>' +

      '<div class="preview">' +
        '<div class="card"><div class="label">Enterprise Mobility Score</div>' +
          '<div class="value">' + report.score + " / 100</div></div>" +
        '<div class="card"><div class="label">Risk Level</div>' +
          '<div class="value" data-risk="' + escapeHtml(report.risk.id) + '">' + escapeHtml(report.risk.label) + "</div>" +
          '<p class="note">' + escapeHtml(report.risk.summary) + "</p></div>" +
        '<div class="card"><div class="label">Est. Annual Productivity Loss</div>' +
          '<div class="value">' + formatCurrency(report.financials.annualProductivityLoss) + "</div></div>" +
        '<div class="card"><div class="label">Est. Avoidable Device Costs</div>' +
          '<div class="value">' + formatCurrency(report.financials.avoidableDeviceCosts) + "</div></div>" +
      "</div>" +

      '<section class="section">' +
        "<h2>Executive Summary</h2>" +
        '<div class="metrics">' +
          '<div class="card"><div class="label">Estimated Risk Level</div><div class="value" data-risk="' + escapeHtml(report.risk.id) + '">' + escapeHtml(report.risk.label) + "</div></div>" +
          '<div class="card"><div class="label">Overall Health Score</div><div class="value">' + report.score + " / 100</div></div>" +
          '<div class="card"><div class="label">Estimated Annual Support Cost</div><div class="value">' + formatCurrency(report.financials.annualSupportCost) + "</div></div>" +
          '<div class="card"><div class="label">Total Estimated Exposure</div><div class="value">' + formatCurrency(report.financials.totalExposure) + "</div></div>" +
        "</div>" +
      "</section>" +

      '<section class="section">' +
        "<h2>Key Findings</h2>" +
        '<ul class="findings">' + findingsHtml + "</ul>" +
      "</section>" +

      '<section class="section">' +
        "<h2>Device Modernisation Roadmap</h2>" +
        '<p class="lede">Six priority workstreams to strengthen reliability, reduce cost, and improve governance.</p>' +
        '<div class="roadmap">' + roadmapHtml + "</div>" +
      "</section>" +

      '<section class="section">' +
        "<h2>Recommendations</h2>" +
        '<ul class="recs">' + recommendationsHtml + "</ul>" +
      "</section>" +

      '<div class="footer"><span>Strike · Confidential</span><span>' + escapeHtml(dateLabel) + "</span></div>" +

      "</div></body></html>"
    );
  }

  function printHtmlDocument(html) {
    var existing = document.getElementById("strikePdfPrintFrame");
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    var iframe = document.createElement("iframe");
    iframe.id = "strikePdfPrintFrame";
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
    document.body.appendChild(iframe);

    var doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    var win = iframe.contentWindow;
    var done = false;

    function triggerPrint() {
      if (done) return;
      done = true;
      try {
        win.focus();
        win.print();
      } catch (err) {
        console.error("Strike PDF print failed:", err);
      }
      setTimeout(function () {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 2000);
    }

    function waitForImagesThenPrint() {
      var images = Array.prototype.slice.call(doc.images || []);
      if (!images.length) {
        setTimeout(triggerPrint, 200);
        return;
      }

      var remaining = images.length;
      var finishOne = function () {
        remaining -= 1;
        if (remaining <= 0) setTimeout(triggerPrint, 150);
      };

      images.forEach(function (img) {
        if (img.complete && img.naturalWidth > 0) {
          finishOne();
          return;
        }
        img.addEventListener("load", finishOne);
        img.addEventListener("error", finishOne);
      });

      /* Safety net so a hung image never blocks print forever */
      setTimeout(triggerPrint, 4000);
    }

    waitForImagesThenPrint();
  }

  function downloadPdfReport() {
    if (!state.report || !state.reportUnlocked) return;
    printHtmlDocument(buildPdfDocumentHtml(state.report));
  }

  function downloadReport() {
    downloadPdfReport();
  }

  /* ═══════════════════════════════════════════════════════════
     MODULE: Lead Capture (Klaviyo Embedded Form)
     Embed: .klaviyo-form-YzpjTn inside #klaviyo-enterprise-form
     On successful submit → onLeadCaptured() → unlock full report
     ═══════════════════════════════════════════════════════════ */

  var leadCaptureBound = false;

  function pickMetaValue(meta, keys) {
    if (!meta) return "";
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (meta[key] != null && String(meta[key]).trim() !== "") {
        return String(meta[key]).trim();
      }
    }
    return "";
  }

  function scrapeLeadFromKlaviyoDom() {
    var root = els.klaviyoEmbed || els.klaviyoForm || document;
    var inputs = root.querySelectorAll("input, select, textarea");
    var found = {
      source: "klaviyo",
      formId: KLAVIYO_FORM_ID,
      email: "",
      firstName: "",
      lastName: "",
      company: "",
      phone: "",
      detectedVia: "dom-fields"
    };

    Array.prototype.forEach.call(inputs, function (el) {
      var name = ((el.getAttribute("name") || "") + " " + (el.getAttribute("autocomplete") || "") + " " + (el.getAttribute("placeholder") || "")).toLowerCase();
      var type = (el.getAttribute("type") || "").toLowerCase();
      var value = (el.value || "").trim();
      if (!value) return;

      if (!found.email && (type === "email" || name.indexOf("email") !== -1)) {
        found.email = value;
      } else if (!found.firstName && (name.indexOf("first") !== -1 || name.indexOf("given-name") !== -1)) {
        found.firstName = value;
      } else if (!found.lastName && (name.indexOf("last") !== -1 || name.indexOf("family-name") !== -1)) {
        found.lastName = value;
      } else if (!found.company && (name.indexOf("company") !== -1 || name.indexOf("organiz") !== -1)) {
        found.company = value;
      } else if (!found.phone && (type === "tel" || name.indexOf("phone") !== -1)) {
        found.phone = value;
      }
    });

    return found;
  }

  function tryReadKlaviyoIdentityEmail() {
    try {
      if (window.klaviyo && typeof window.klaviyo.get === "function") {
        /* Older snippets may expose identity asynchronously; best-effort only */
      }
      if (window._learnq && window._learnq.account) {
        /* no stable sync email API — intentionally empty */
      }
    } catch (e) { /* ignore */ }
    return "";
  }

  /**
   * Called after lead details are captured successfully via Klaviyo.
   * Reveal the locked report, build the assessment payload, and sync to Sheets.
   */
  function onLeadCaptured(leadData) {
    if (state.reportUnlocked) return;

    /* Prefer event meta, then live form fields, then any prior/persisted lead */
    var fromEvent = leadData || {};
    var fromDom = scrapeLeadFromKlaviyoDom();
    state.lead = mergeLead({
      source: fromEvent.source || fromDom.source || "klaviyo",
      formId: fromEvent.formId || KLAVIYO_FORM_ID,
      email: fromEvent.email || fromDom.email || tryReadKlaviyoIdentityEmail(),
      firstName: fromEvent.firstName || fromDom.firstName || "",
      lastName: fromEvent.lastName || fromDom.lastName || "",
      company: fromEvent.company || fromDom.company || "",
      phone: fromEvent.phone || fromDom.phone || "",
      detectedVia: fromEvent.detectedVia || fromDom.detectedVia
    });

    ensureReportBuilt();

    if (!state.assessmentId) {
      state.assessmentId = generateAssessmentId();
    }

    var payload = buildAssessmentPayload();

    saveState();
    unlockFullReport(true);

    /* Fire-and-forget: UI already unlocked; duplicates blocked by assessmentId */
    sendAssessmentToWebhook(payload);
  }

  function extractLeadFromKlaviyoDetail(detail) {
    if (!detail) return { source: "klaviyo", formId: KLAVIYO_FORM_ID };
    var meta = detail.metaData || detail.meta || detail.data || {};
    if (meta.values && typeof meta.values === "object") {
      meta = Object.assign({}, meta, meta.values);
    }
    return {
      source: "klaviyo",
      formId: detail.formId || detail.form_id || KLAVIYO_FORM_ID,
      email: normalizeEmail(
        pickMetaValue(meta, ["$email", "email", "Email", "company_email", "Company Email"])
      ),
      firstName: pickMetaValue(meta, ["$first_name", "first_name", "firstName", "First Name"]),
      lastName: pickMetaValue(meta, ["$last_name", "last_name", "lastName", "Last Name"]),
      company: pickMetaValue(meta, ["organization", "company", "Company", "$organization"]),
      phone: pickMetaValue(meta, ["$phone_number", "phone_number", "phone", "Phone Number", "Phone"])
    };
  }

  function isOurKlaviyoForm(detail) {
    if (!detail) return true;
    var id = detail.formId || detail.form_id || "";
    if (!id) return true;
    return String(id) === KLAVIYO_FORM_ID;
  }

  function bindKlaviyoLeadCapture() {
    if (leadCaptureBound) return;
    leadCaptureBound = true;

    /* Snapshot field values on input so DOM-success fallback still has email */
    if (els.klaviyoEmbed) {
      els.klaviyoEmbed.addEventListener("change", function () {
        var snapped = scrapeLeadFromKlaviyoDom();
        if (snapped.email || snapped.firstName || snapped.company) {
          state.lead = mergeLead(snapped);
          saveState();
        }
      }, true);
      els.klaviyoEmbed.addEventListener("blur", function (e) {
        if (!e.target || !e.target.matches || !e.target.matches("input, select, textarea")) return;
        var snapped = scrapeLeadFromKlaviyoDom();
        if (snapped.email) {
          state.lead = mergeLead(snapped);
          saveState();
        }
      }, true);
    }

    /* Official Klaviyo onsite form events */
    window.addEventListener("klaviyoForms", function (e) {
      var detail = e.detail || {};
      var type = detail.type || "";

      if ((type === "submit" || type === "submitted" || type === "success") && isOurKlaviyoForm(detail)) {
        onLeadCaptured(extractLeadFromKlaviyoDetail(detail));
      }
    });

    /* Fallback: observe success state inside the embed container */
    if (els.klaviyoEmbed && typeof MutationObserver !== "undefined") {
      var observer = new MutationObserver(function () {
        if (state.reportUnlocked) return;
        var success = els.klaviyoEmbed.querySelector(
          '[data-testid="form-success"], .needsclick.kl-private-reset-css-Xuajs1, .klaviyo-form-success'
        );
        var successText = (els.klaviyoEmbed.textContent || "").toLowerCase();
        var looksSuccessful =
          !!success ||
          successText.indexOf("thanks") !== -1 ||
          successText.indexOf("thank you") !== -1 ||
          successText.indexOf("success") !== -1;

        if (looksSuccessful && els.klaviyoEmbed.querySelector("form") === null) {
          var scraped = scrapeLeadFromKlaviyoDom();
          scraped.detectedVia = "dom";
          onLeadCaptured(scraped);
        }
      });

      observer.observe(els.klaviyoEmbed, {
        childList: true,
        subtree: true,
        characterData: true
      });

      if (typeof ResizeObserver !== "undefined" && els.klaviyoForm) {
        var resizeObs = new ResizeObserver(function () {
          sizeReportGateToForm();
        });
        resizeObs.observe(els.klaviyoForm);
      }
    }
  }

  /* ═══════════════════════════════════════════════════════════
     Events & Init
     ═══════════════════════════════════════════════════════════ */

  function bindEvents() {
    els.btnNext.addEventListener("click", goNext);
    els.btnBack.addEventListener("click", goBack);
    if (els.btnDownload) els.btnDownload.addEventListener("click", downloadPdfReport);
    if (els.btnRetake) els.btnRetake.addEventListener("click", retakeAssessment);
    bindKlaviyoLeadCapture();
    bindIntroCta();

    els.metricsForm.addEventListener("input", function () {
      readMetricsFromForm();
      updateNavButtons();
    });
    els.metricsForm.addEventListener("change", function () {
      readMetricsFromForm();
      updateNavButtons();
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Enter" && state.step === "question" && !els.btnNext.disabled) {
        if (document.activeElement && document.activeElement.classList.contains("rating-option")) {
          e.preventDefault();
          goNext();
        }
      }
    });
  }

  /* ═══════════════════════════════════════════════════════════
     Intro CTA → reveal assessment
     Add data-start-assessment to your Start Assessment button.
     Add data-assessment-intro to the intro section/block to hide.
     Optionally wrap the assessment Shopify section with
     data-assessment-host so the whole section stays hidden.
     ═══════════════════════════════════════════════════════════ */

  function showAssessmentElement(el) {
    if (!el) return;
    el.hidden = false;
    el.removeAttribute("hidden");
    el.setAttribute("aria-hidden", "false");
    el.classList.add("is-started");
    el.style.setProperty("display", "block", "important");
  }

  function getAssessmentHost() {
    var app = els.app || document.getElementById("assessmentApp");
    if (app && app.closest) {
      var nested = app.closest("[data-assessment-host]");
      if (nested) return nested;
    }
    return document.querySelector("[data-assessment-host]") || app || null;
  }

  function revealAssessment(options) {
    options = options || {};
    var scroll = options.scroll !== false;

    /* Re-query in case Shopify re-rendered section HTML */
    els.app = document.getElementById("assessmentApp") || els.app;

    /* Hide intro block(s) */
    Array.prototype.forEach.call(document.querySelectorAll("[data-assessment-intro]"), function (el) {
      el.hidden = true;
      el.setAttribute("aria-hidden", "true");
      el.classList.add("is-assessment-intro-hidden");
      el.style.setProperty("display", "none", "important");
    });

    /* Show every assessment host + the app itself */
    Array.prototype.forEach.call(document.querySelectorAll("[data-assessment-host]"), function (el) {
      showAssessmentElement(el);
    });
    showAssessmentElement(els.app);

    if (typeof startAssessment === "function") {
      try {
        startAssessment();
      } catch (err) {
        console.error("Strike assessment failed to start:", err);
      }
    }

    var scrollTarget = getAssessmentHost() || els.app;
    if (scroll && scrollTarget) {
      setTimeout(function () {
        scrollToElementWithOffset(scrollTarget, SCROLL_OFFSET);
      }, 80);
    }
  }

  function bindIntroCta() {
    document.addEventListener("click", function (e) {
      var trigger = e.target.closest(
        "[data-start-assessment], #startAssessment, .js-start-assessment, a[href='#start-assessment'], a[href='#assessment'], .enterprise-btn[data-start-assessment]"
      );
      if (!trigger) return;

      e.preventDefault();
      revealAssessment({ scroll: true });
    });
  }

  function init() {
    cacheElements();
    loadState();

    /* Rebuild report after refresh when unlock was already earned */
    if (state.reportUnlocked) {
      ensureReportBuilt();
      if (state.report && state.lead) {
        buildAssessmentPayload();
      }
    }

    bindEvents();

    /* Stay hidden until intro CTA — prepare Q1 in memory only */
    state.step = "question";
    if (typeof state.questionIndex !== "number" || state.questionIndex < 0) {
      state.questionIndex = 0;
    }

    window.StrikeMobilityAssessment = {
      ScoringEngine: ScoringEngine,
      QUESTIONS: QUESTIONS,
      SECTIONS: SECTIONS,
      onLeadCaptured: onLeadCaptured,
      unlockFullReport: unlockFullReport,
      start: revealAssessment,
      revealAssessment: revealAssessment,
      buildAssessmentPayload: buildAssessmentPayload,
      getAssessmentPayload: function () {
        if (state.lastAssessmentPayload) return state.lastAssessmentPayload;
        try {
          var raw = localStorage.getItem(ASSESSMENT_PAYLOAD_KEY);
          if (raw) return JSON.parse(raw);
        } catch (e) { /* ignore */ }
        return buildAssessmentPayload();
      },
      getState: function () {
        var copy = JSON.parse(JSON.stringify(state));
        /* Do not expose personalised report payload while locked */
        if (!copy.reportUnlocked) {
          copy.report = null;
          copy.lastAssessmentPayload = null;
        }
        return copy;
      },
      reset: function () {
        retakeAssessment();
        /* Also restore intro if present */
        if (els.app) {
          els.app.classList.remove("is-started", "is-report-unlocked");
          els.app.hidden = true;
          els.app.setAttribute("aria-hidden", "true");
        }
        var host = getAssessmentHost();
        if (host && host !== els.app) {
          host.classList.remove("is-started");
        }
        Array.prototype.forEach.call(document.querySelectorAll("[data-assessment-intro]"), function (el) {
          el.hidden = false;
          el.removeAttribute("hidden");
          el.setAttribute("aria-hidden", "false");
          el.classList.remove("is-assessment-intro-hidden");
          el.style.display = "";
        });
      }
    };
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
